import {useMemo} from 'react';
import {classifyBiomarker, type BiomarkerClassification} from '@/shared/biomarkers/classify';
import type {CustomBiomarkerConfig} from '@/shared/types/biomarker';

// Re-export the shared shapes so existing importers of these types from this
// hook keep compiling.
export type {ProgressBarSection, OptimalZoneInfo, ResolvedColors} from '@/shared/biomarkers/classify';

interface UseBiomarkerZonesParams {
    rangeMin: number;
    rangeMax: number;
    rawRefRange: string;
    customConfig: CustomBiomarkerConfig | null | undefined;
    isBinary?: boolean;
    /** Current result value; needed for indicatorPercent.  Pass undefined
     *  for layouts that only need zones/labels (e.g. dashboard summary). */
    value?: number;
    /** For binary tests, the resolved Negative/Positive status. */
    binaryStatus?: 'Negative' | 'Positive' | null;
}

// Result now extends the full classification so callers can additionally read
// status/tier/color if they want. Existing fields are unchanged.
export type UseBiomarkerZonesResult = BiomarkerClassification;

/**
 * Shared hook for biomarker zone calculations.
 *
 * Thin React wrapper over the pure `classifyBiomarker` core, which is the
 * single source of truth for a biomarker's status, severity tier, chart-zone
 * colors, indicator position, and count bucket. The hook preserves its input
 * signature AND its historical output field names (so chart JSX doesn't
 * change) while additionally exposing `status`, `tier`, and `color`.
 *
 * Used by BiomarkerChartCard, LabResults, and any other component that
 * renders biomarker range indicators.
 */
export function useBiomarkerZones({
    rangeMin,
    rangeMax,
    rawRefRange,
    customConfig,
    isBinary = false,
    value,
    binaryStatus = null,
}: UseBiomarkerZonesParams): UseBiomarkerZonesResult {
    return useMemo(
        () =>
            classifyBiomarker({
                rangeMin,
                rangeMax,
                rawRefRange,
                customConfig,
                isBinary,
                value,
                binaryStatus,
                abnormal: null,
            }),
        [rangeMin, rangeMax, rawRefRange, customConfig, isBinary, value, binaryStatus]
    );
}
